import { navigate } from 'gatsby';
import { isEmpty, mergeRight } from 'ramda';
import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { ThemeProvider } from 'styled-components';

import FlowFooter from '@ui/atoms/FlowFooter';
import FlowGrid from '@ui/atoms/FlowGrid';
import FlowPageContainer from '@ui/atoms/FlowPageContainer';

import NewFlowHeader from '@ui/organisms/NewFlowHeader';

import useOnboardedRoute from '@hooks/useOnboardedRoute';
import { useTracking } from '@hooks/useTracking';

import {
	getFinalStepIndex,
	getFirstStep,
	getNextStepForKey,
	getPathForCurrentStep,
	getPathPrefixFromPathname,
	getPreviousStepForKey,
	getStepForKey,
	getStepForPath,
	getStepIndex,
	getStepsForFlow,
} from '@utils/flowHelpers';
import { exists } from '@utils/general';

import { FlowContextProvider } from '@providers/FlowContextProvider';

import { FlowIdTypes, FormData } from '@folx-types/flow';
import { FlowStepData } from '@folx-types/flow-steps';

import { getThemeForFlow } from '@theme/FlowThemes';

interface IAppointmentBookingRouteProps {
	path: string;
	children?: ReactNode;
	serviceType?: FlowIdTypes;
}

const AppointmentBookingRoute: FC<IAppointmentBookingRouteProps> = ({
	children: nestedRouter,
	path,
	serviceType,
	...props
}) => {
	const {
		props: { children: routes },
	} = nestedRouter;
	const [loaded, setLoaded] = useState(false);
	const [returnKey, setReturnKey] = useState('');
	const [isPaymentWaived, setIsPaymentWaived] = useState(false);

	const parentRef = useRef<HTMLDivElement>(null);

	const FLOW_ID: FlowIdTypes = serviceType as FlowIdTypes;
	const FLOW_PATH = `appointment_booking/${serviceType}`;

	const currentPath = getPathForCurrentStep(props['*']);
	const stepsForFlow = getStepsForFlow(routes);
	const firstStep = getFirstStep(stepsForFlow) ?? ({} as FlowStepData);
	const currentStep = getStepForPath(currentPath, stepsForFlow) ?? ({} as FlowStepData);
	const currentIndex = getStepIndex(currentStep, stepsForFlow);
	const finalIndex = getFinalStepIndex(stepsForFlow);
	const isSecureRoute = !!currentStep?.secure;

	const {
		loaded: formLoaded,
		loggedIn,
		formData,
		submitFormData,
		location,
	} = useOnboardedRoute({
		flowId: FLOW_ID,
		flowPath: FLOW_PATH,
		firstStepPath: firstStep.path,
		currentStepKey: currentStep.stepKey,
		isSecureRoute,
		onboardedFlowName: 'membership',
	});

	const { trackEvent } = useTracking();
	useEffect(() => {
		trackEvent('view-onboarding-step', {
			step_name: currentStep?.stepKey,
			flow_id: FLOW_ID,
		});
	}, [currentStep]);

	useEffect(() => {
		setLoaded(formLoaded);
	}, [formLoaded]);

	const handleFormSubmission = async (data: FormData, stepKey: string) => {
		console.log('handleFormSubmission');
		if (loggedIn && !isEmpty(data)) {
			await submitFormData(data);
		}
		const combinedData = mergeRight(formData, data);
		const pathPrefix = getPathPrefixFromPathname(location.pathname, FLOW_PATH);
		const nextStep = getNextStepForKey(
			stepsForFlow,
			FLOW_ID,
			currentStep.stepKey,
			combinedData,
			isPaymentWaived
		);

		if (returnKey) {
			const returnStep = getStepForKey(stepsForFlow, returnKey);
			setReturnKey('');
			return navigate(`/${pathPrefix}/${FLOW_PATH}/${returnStep?.path}`);
		}

		navigate(`/${pathPrefix}/${FLOW_PATH}/${nextStep?.path}`);
	};

	const handleNavBack = (stepKey: string) => {
		const previousStep = getPreviousStepForKey(stepsForFlow, stepKey);
		const pathPrefix = getPathPrefixFromPathname(location.pathname, FLOW_PATH);
		navigate(`/${pathPrefix}/${FLOW_PATH}/${previousStep.path}`, {
			state: { reverse: true },
		});
	};

	const handleNavNext = (currentStepKey: string) => {
		const nextStep = getNextStepForKey(stepsForFlow, FLOW_ID, currentStepKey, formData);
		const pathPrefix = getPathPrefixFromPathname(location.pathname, FLOW_PATH);
		navigate(`/${pathPrefix}/${FLOW_PATH}/${nextStep.path}`, {
			state: { reverse: false },
		});
	};

	const handleTemporaryNavigation = (stepKey: string, returnKey: string) => {
		setReturnKey(returnKey);
		const pathPrefix = getPathPrefixFromPathname(location.pathname, FLOW_PATH);
		const step = getStepForKey(stepsForFlow, stepKey);
		return navigate(`/${pathPrefix}/${FLOW_PATH}/${step?.path}`);
	};

	return (
		loaded && (
			<FlowContextProvider
				value={{
					destinationFlow: FLOW_PATH,
					existingFormData: formData,
					flow: FLOW_PATH,
					flowId: FLOW_ID,
					handleFormSubmission,
					handleNavBack,
					handleNavNext,
					handleTemporaryNavigation,
					submitFormData,
					hideNavBack: exists(returnKey),
					parentRef,
					isPaymentWaived,
					setIsPaymentWaived,
				}}>
				<ThemeProvider theme={getThemeForFlow('hrt_t')}>
					<FlowPageContainer
						ref={parentRef}
						header={
							<NewFlowHeader
								flowName="Appointment Booking"
								totalSteps={finalIndex}
								step={currentIndex}
							/>
						}
						footer={<FlowFooter />}>
						{loaded ? <FlowGrid>{nestedRouter}</FlowGrid> : <div />}
					</FlowPageContainer>
				</ThemeProvider>
			</FlowContextProvider>
		)
	);
};

export default AppointmentBookingRoute;
